// ═══════════════════════════════════════════════════════════════════
// pushClient (Apr28 v17)
// ═══════════════════════════════════════════════════════════════════
// Utility functions for browser push notification subscribe/unsubscribe.
//
// Two main entry points:
//   isPushSupported()              — feature-detect (true on most modern browsers + iOS 16.4+ PWAs)
//   getPushPermissionState()       — 'default' | 'granted' | 'denied'
//   subscribeToPush()              — full subscribe flow + send to backend
//   unsubscribeFromPush()          — fully revoke
//   isSubscribed()                 — check whether browser has an active sub
//
// Notes on iOS:
//   - Push only works on installed PWAs (iOS 16.4+) — fails silently otherwise
//   - We feature-detect both Notification + PushManager + ServiceWorker
//   - In-browser Safari (not installed) returns isPushSupported() = false
// ═══════════════════════════════════════════════════════════════════

const API = process.env.REACT_APP_API_URL || 'https://api.glitzandglambyjyoti.com';

// Convert URL-safe base64 → Uint8Array (browser pushManager expects this)
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function isPushSupported() {
  if (typeof window === 'undefined') return false;
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function getPushPermissionState() {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

// Detect basic platform for analytics/segmentation
function detectPlatform() {
  if (typeof navigator === 'undefined') return 'web';
  const ua = navigator.userAgent.toLowerCase();
  if (/android/.test(ua)) return 'android';
  if (/iphone|ipad|ipod/.test(ua)) return 'ios';
  return 'desktop';
}

// Check if THIS browser is currently subscribed (does NOT prompt user)
export async function isSubscribed() {
  if (!isPushSupported()) return false;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch (_) {
    return false;
  }
}

// Full subscribe flow:
//   1. Request notification permission (if not already granted)
//   2. Subscribe via pushManager with VAPID public key from backend
//   3. Send subscription to backend for storage
// Returns: { ok: true } on success, { ok: false, reason: '...' } on failure.
export async function subscribeToPush() {
  if (!isPushSupported()) {
    return { ok: false, reason: 'unsupported' };
  }

  // 1. Permission check / prompt
  let permission = Notification.permission;
  if (permission === 'default') {
    try {
      permission = await Notification.requestPermission();
    } catch (e) {
      return { ok: false, reason: 'permission-error', error: e?.message };
    }
  }
  if (permission !== 'granted') {
    return { ok: false, reason: 'permission-denied' };
  }

  // 2. Get VAPID public key
  let vapidPublic;
  try {
    const res = await fetch(`${API}/api/push/vapid-public`);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      return { ok: false, reason: 'no-vapid', error: err.message };
    }
    const data = await res.json();
    vapidPublic = data.publicKey;
    if (!vapidPublic) return { ok: false, reason: 'no-vapid' };
  } catch (e) {
    return { ok: false, reason: 'vapid-fetch', error: e?.message };
  }

  // 3. Subscribe via pushManager
  let subscription;
  try {
    const registration = await navigator.serviceWorker.ready;
    // Reuse existing subscription if present (prevents 'already subscribed' errors)
    subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublic),
      });
    }
  } catch (e) {
    return { ok: false, reason: 'subscribe-failed', error: e?.message };
  }

  // 4. Send to backend
  try {
    const token = localStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;

    const res = await fetch(`${API}/api/push/subscribe`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        subscription: subscription.toJSON(),
        userAgent: navigator.userAgent,
        platform: detectPlatform(),
      }),
    });
    if (!res.ok) {
      return { ok: false, reason: 'backend-store', status: res.status };
    }
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: 'backend-error', error: e?.message };
  }
}

export async function unsubscribeFromPush() {
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' };
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return { ok: true, alreadyUnsubscribed: true };

    const endpoint = subscription.endpoint;

    // Tell backend first (non-blocking — if it fails, browser-side unsub still proceeds)
    fetch(`${API}/api/push/unsubscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint }),
    }).catch(() => {});

    // Then unsubscribe in browser
    await subscription.unsubscribe();
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: 'unsubscribe-error', error: e?.message };
  }
}
